const TABLE_MARKER_REGEX = /\|\|\|TABLE:([a-zA-Z0-9_-]+)\|rows=(\d+)\|cols=(\d+)(?:\|colw=([^|]*))?(?:\|seed=([^|]*))?\|\|\|/g;
const SEED_ENTRY_REGEX = /(\d+)_(\d+)(!)?\{([^}]*)\}/g;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const normalizePositiveInt = (value, fallback = 1) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const normalizeWeight = (value, fallback = 1) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.min(8, Math.max(0.2, parsed));
};

const parseColumnWeights = (raw, cols) => {
  const weights = Array.from({ length: cols }, () => 1);
  if (!raw) return weights;

  const trimmed = String(raw).trim();
  if (!trimmed) return weights;

  if (trimmed.includes(':')) {
    trimmed.split(',').forEach((pair) => {
      const [indexRaw, weightRaw] = pair.split(':').map((part) => part.trim());
      const index = Number.parseInt(indexRaw, 10);
      if (!Number.isFinite(index) || index < 0 || index >= cols) return;
      weights[index] = normalizeWeight(weightRaw, 1);
    });
    return weights;
  }

  trimmed.split(',').forEach((weightRaw, idx) => {
    if (idx >= cols) return;
    const value = weightRaw.trim();
    if (!value) return;
    weights[idx] = normalizeWeight(value, 1);
  });

  return weights;
};

const serializeColumnWeights = (weights) =>
  weights.map((weight) => Number(weight).toFixed(3).replace(/\.?0+$/, '')).join(',');

const encodeCellValue = (value) =>
  String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n/g, '\\n');

const parseSeed = (seedRaw, rows, cols) => {
  const valuesByIndex = new Map();
  if (!seedRaw) return valuesByIndex;

  SEED_ENTRY_REGEX.lastIndex = 0;
  let match;
  while ((match = SEED_ENTRY_REGEX.exec(seedRaw)) !== null) {
    const row = Number.parseInt(match[1], 10);
    const col = Number.parseInt(match[2], 10);
    const isBold = Boolean(match[3]);
    const value = match[4] ?? '';

    if (Number.isNaN(row) || Number.isNaN(col)) {
      continue;
    }

    if (row < 0 || col < 0 || row >= rows || col >= cols) {
      // Fail-safe: ignore out-of-range seed entries.
      continue;
    }

    valuesByIndex.set(`${row}_${col}`, { value, isBold });
  }

  return valuesByIndex;
};

const getTableFieldId = (tableId, row, col) => {
  const random = Math.random().toString(36).slice(2, 9);
  return `table_${tableId}_${row}_${col}_${random}`;
};

const renderTableHtml = (tableId, rows, cols, columnWeights, seedRaw) => {
  const seedMap = parseSeed(seedRaw, rows, cols);
  let tableRowsHtml = '';

  for (let row = 0; row < rows; row += 1) {
    let cellsHtml = '';
    for (let col = 0; col < cols; col += 1) {
      const cellIndex = `${row}_${col}`;
      const cellSeed = seedMap.get(cellIndex) || { value: '', isBold: false };
      const initialValue = cellSeed.value || '';
      const isBold = cellSeed.isBold;
      const fieldId = getTableFieldId(tableId, row, col);
      const escapedInitialValue = escapeHtml(initialValue);
      const boldClass = isBold ? ' inline-table-input-bold' : '';
      const boldAttr = isBold ? '1' : '0';

      cellsHtml += `<td class="inline-table-cell"><textarea class="inline-input inline-table-input${boldClass}" data-field-id="${fieldId}" data-table-id="${tableId}" data-cell-index="${cellIndex}" data-cell-bold="${boldAttr}" placeholder="${cellIndex}">${escapedInitialValue}</textarea></td>`;
    }
    tableRowsHtml += `<tr>${cellsHtml}</tr>`;
  }

  const colwRaw = serializeColumnWeights(columnWeights);
  return `<span class="inline-field inline-table-field" contenteditable="false"><span class="inline-table" data-table-id="${tableId}" data-rows="${rows}" data-cols="${cols}" data-table-colw="${colwRaw}"><table class="inline-table-grid"><tbody>${tableRowsHtml}</tbody></table></span></span>`;
};

export const renderTablePlaceholders = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  return text.replace(TABLE_MARKER_REGEX, (match, tableIdRaw, rowsRaw, colsRaw, colwRaw = '', seedRaw = '') => {
    const tableId = escapeHtml(tableIdRaw);
    const rows = normalizePositiveInt(rowsRaw, 1);
    const cols = normalizePositiveInt(colsRaw, 1);
    const columnWeights = parseColumnWeights(colwRaw, cols);
    return renderTableHtml(tableId, rows, cols, columnWeights, seedRaw);
  });
};

const getCellSortValue = (cellIndex) => {
  const [rowRaw, colRaw] = String(cellIndex || '').split('_');
  const row = Number.parseInt(rowRaw, 10);
  const col = Number.parseInt(colRaw, 10);
  const safeRow = Number.isFinite(row) ? row : Number.MAX_SAFE_INTEGER;
  const safeCol = Number.isFinite(col) ? col : Number.MAX_SAFE_INTEGER;
  return safeRow * 100000 + safeCol;
};

export const serializeTableField = (fieldElement) => {
  const tableRoot = fieldElement?.querySelector?.('.inline-table');
  if (!tableRoot) return null;

  const tableId = tableRoot.dataset.tableId || 'table';
  const tableCols = normalizePositiveInt(tableRoot.dataset.cols, 1);
  const columnWeights = parseColumnWeights(tableRoot.dataset.tableColw, tableCols);
  const serializedColw = serializeColumnWeights(columnWeights);
  const cells = Array.from(tableRoot.querySelectorAll('.inline-table-input'));
  cells.sort((a, b) => {
    const aIndex = a.dataset.cellIndex || '';
    const bIndex = b.dataset.cellIndex || '';
    return getCellSortValue(aIndex) - getCellSortValue(bIndex);
  });

  const serializedCells = cells.map((cell) => {
    const index = cell.dataset.cellIndex || '';
    const boldFlag = cell.dataset.cellBold === '1' ? '!' : '';
    let value = cell.value || '';
    value = value.replace(/prefix-[^\s,]+\s*/g, '');
    value = value.replace(/key-[^\s,]+\s*/g, '').trim();
    const encoded = encodeCellValue(value);
    return `${index}${boldFlag}_${encoded}`;
  });

  return `${tableId}|colw=${serializedColw}|${serializedCells.join(';')}`;
};
